// ---------------------------------------------------------------------------
// Financeiro · Régua de cobrança — formatação pura (sem estado, sem API).
// Promovido de Dunning/utils quando a Timeline de cobranças virou tela própria
// e passou a consumir os mesmos helpers (2026-08-14). Arquivo separado do
// `format.js` vizinho, que é do domínio bancário (handoff 0079).
//
// Regra de ouro do handoff 0090: nulo é "—", nunca `R$ 0,00` nem `0 dias`.
// Datas-dia do backend são "YYYY-MM-DD" (dia civil de Goiânia — exibir como
// está, sem Date/timezone); timestamps são ISO UTC (exibir no fuso local).
// ---------------------------------------------------------------------------

// formatBRL promovido para utils/format.js em 2026-09-02 (billing passou a
// precisar dele, handoff 0105). Import + re-export para não tocar os
// consumidores da régua/timeline — o path deles continua este arquivo.
import { formatBRL } from '../../../utils/format';

export { formatBRL };

// Forma curta dos cartões de valor do cabeçalho: `R$ 12,4 mil` (UI_SPEC).
export function formatBRLShort(value) {
   if (value === null || value === undefined) return '—';
   const number = Number(value);
   if (number >= 1000) {
      return `R$ ${(number / 1000).toLocaleString('pt-BR', {
         minimumFractionDigits: 1,
         maximumFractionDigits: 1,
      })} mil`;
   }
   return formatBRL(number);
}

// "YYYY-MM-DD" → "DD/MM/AAAA" por fatiamento (NUNCA via new Date(), que
// deslocaria o dia civil pelo fuso).
export function formatDateBR(isoDay) {
   if (!isoDay) return '—';
   const [year, month, day] = String(isoDay).slice(0, 10).split('-');
   if (!year || !month || !day) return '—';
   return `${day}/${month}/${year}`;
}

// CNPJ/CPF mascarado a partir dos dígitos crus do backend.
export function formatDoc(doc) {
   const digits = String(doc ?? '').replace(/\D/g, '');
   if (digits.length === 14) {
      return digits.replace(
         /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
         '$1.$2.$3/$4-$5'
      );
   }
   if (digits.length === 11) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
   }
   return doc ?? '—';
}

export function getInitials(name) {
   if (!name) return '—';
   return String(name)
      .split(' ')
      .filter((word) => word.length > 2)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase();
}

// Dia civil local de hoje em "YYYY-MM-DD" (para comparar com as datas-dia do
// backend sem passar por UTC).
export function todayIso() {
   const now = new Date();
   const month = String(now.getMonth() + 1).padStart(2, '0');
   const day = String(now.getDate()).padStart(2, '0');
   return `${now.getFullYear()}-${month}-${day}`;
}

// Diferença em dias entre dois dias civis "YYYY-MM-DD" (b - a). Parse em UTC
// nos DOIS lados — a diferença fica imune a fuso/horário de verão.
export function daysBetween(a, b) {
   if (!a || !b) return null;
   const parsedA = Date.parse(`${String(a).slice(0, 10)}T00:00:00Z`);
   const parsedB = Date.parse(`${String(b).slice(0, 10)}T00:00:00Z`);
   if (Number.isNaN(parsedA) || Number.isNaN(parsedB)) return null;
   return Math.round((parsedB - parsedA) / 86400000);
}

// Soma N dias corridos a um dia civil "YYYY-MM-DD". Aritmética ao meio-dia
// UTC — imune a fuso e horário de verão.
export function addDays(isoDay, n) {
   if (!isoDay) return null;
   const parsed = Date.parse(`${String(isoDay).slice(0, 10)}T12:00:00Z`);
   if (Number.isNaN(parsed)) return null;
   return new Date(parsed + n * 86400000).toISOString().slice(0, 10);
}

// Tamanho de anexo em unidade legível ("182 KB", "1,4 MB"). Base 1024 — é a
// mesma que o limite de 10MB do backend usa (handoff 0096).
export function formatFileSize(bytes) {
   const size = Number(bytes);
   if (bytes === null || bytes === undefined || !Number.isFinite(size)) {
      return '—';
   }
   if (size < 1024) return `${size} B`;
   if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
   return `${(size / (1024 * 1024)).toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
   })} MB`;
}

// "1 dia" / "N dias" — helper único dos rótulos e tooltips da linha do tempo
// (adendo 01, regra 5).
export function formatDayCount(n) {
   return `${n} ${n === 1 ? 'dia' : 'dias'}`;
}

// Timestamp ISO UTC → "Hoje às 14:32" / "Ontem às 09:05" / "12/08/2026 às
// 14:32", no fuso LOCAL do navegador (contrato manda exibir convertido).
export function formatTimestamp(iso) {
   if (!iso) return '—';
   const date = new Date(iso);
   if (Number.isNaN(date.getTime())) return '—';

   const month = String(date.getMonth() + 1).padStart(2, '0');
   const dayOfMonth = String(date.getDate()).padStart(2, '0');
   const localDay = `${date.getFullYear()}-${month}-${dayOfMonth}`;
   const time = `${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes()
   ).padStart(2, '0')}`;

   const diff = daysBetween(localDay, todayIso());
   if (diff === 0) return `Hoje às ${time}`;
   if (diff === 1) return `Ontem às ${time}`;
   return `${formatDateBR(localDay)} às ${time}`;
}
